import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { apiJson, assetUrl } from "../utils/api";
import useSeo from "../hooks/useSeo";
import useUserMaritalStatus from "../hooks/useUserMaritalStatus";
import SpeechInputButton from "../components/SpeechInputButton";
import AiModuleTabs from "../components/AiModuleTabs";
import { speakText, stopSpeaking, ttsSupported } from "../utils/speechTts";

function MatchRecommendCards({ cards }) {
  if (!cards?.length) return null;
  return (
    <div className="mt-3 space-y-2">
      <div className="text-xs font-semibold text-rose-600">为你推荐</div>
      {cards.map((card) => (
        <div
          key={card.user_id || card.id}
          className="flex gap-3 items-center bg-white border border-rose-100 rounded-xl p-3 shadow-sm"
        >
          <img
            src={assetUrl(card.avatar) || "https://picsum.photos/56"}
            alt=""
            className="w-12 h-12 rounded-full object-cover shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold text-gray-900 text-sm truncate">
                {card.name || "会员"}
                {card.age ? ` · ${card.age}` : ""}
              </div>
              <div className="text-rose-600 font-bold text-sm shrink-0">
                {card.match_score}%
              </div>
            </div>
            <div className="text-xs text-gray-500 truncate">
              {[card.city, card.job].filter(Boolean).join(" · ") || "资料完善中"}
            </div>
            {!!card.advantages?.length && (
              <div className="text-[11px] text-emerald-700 mt-1 truncate">
                {card.advantages.slice(0, 2).join(" · ")}
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              <Link
                to={`/user/${card.user_id || card.id}`}
                className="px-2.5 py-1 rounded-full bg-rose-500 text-white text-[11px]"
              >
                查看资料
              </Link>
              <Link
                to={`/ai/match?target=${card.user_id || card.id}`}
                className="px-2.5 py-1 rounded-full border border-rose-300 text-rose-600 text-[11px]"
              >
                AI匹配分析
              </Link>
            </div>
          </div>
        </div>
      ))}
      <Link
        to="/ai/match"
        className="inline-block text-xs text-rose-600 underline pt-1"
      >
        查看更多匹配推荐 →
      </Link>
    </div>
  );
}

function ProfileReportCard({ profile }) {
  const [valuesOpen, setValuesOpen] = useState(false);
  if (!profile?.ai_report) return null;
  const report = profile.ai_report;
  const personality = report.personality || {};
  const values = report.values || {};
  const relationship = report.relationship || {};

  const valuesSummary = [
    values.family_view || profile.family_view,
    values.marriage_view || profile.marriage_view,
    values.lifestyle || profile.lifestyle,
  ]
    .filter(Boolean)
    .join("；");
  const valuesPreview =
    valuesSummary.length > 42 ? `${valuesSummary.slice(0, 42)}…` : valuesSummary || "暂未生成摘要";

  return (
    <div className="rounded-2xl border border-rose-100 bg-gradient-to-br from-white to-rose-50 p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-base font-bold text-gray-900">个人婚恋画像</h3>
          <p className="text-xs text-gray-500 mt-1">完成度 {profile.interview_progress || 0}%</p>
        </div>
        <div className="text-center shrink-0">
          <div className="text-xl font-extrabold text-rose-600">{profile.profile_score || 0}</div>
          <div className="text-[10px] text-gray-400">完整度</div>
        </div>
      </div>

      {report.summary_text && (
        <p className="text-xs text-gray-700 leading-relaxed mb-3 bg-white/70 rounded-xl p-2.5 line-clamp-4">
          {report.summary_text}
        </p>
      )}

      <div className="space-y-2 text-sm">
        <div className="bg-white rounded-xl p-3 border border-gray-100">
          <div className="text-xs text-gray-400 mb-1">性格</div>
          <div className="font-semibold text-gray-800 text-sm">
            {personality.personality_type ||
              personality.summary ||
              profile.personality_type ||
              profile.personality}
          </div>
          <div className="mt-1.5 text-gray-600 text-xs">
            沟通：{personality.communication_style || profile.communication_style || "—"}
          </div>
        </div>
        <div
          className="bg-white rounded-xl p-3 border border-gray-100 cursor-pointer hover:border-rose-200"
          title={valuesSummary || undefined}
          onClick={() => setValuesOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setValuesOpen(true);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="text-xs text-gray-400">婚恋价值观</div>
            <span className="text-[11px] text-rose-600">查看全部</span>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">{valuesPreview}</p>
        </div>
      </div>

      <div className="mt-3 space-y-2 text-xs">
        <div className="bg-emerald-50 rounded-xl p-2.5">
          <div className="font-semibold text-emerald-700 mb-1">优势</div>
          <ul className="space-y-1 text-emerald-900">
            {(relationship.strengths || profile.strengths || []).slice(0, 3).map((item) => (
              <li key={item}>✓ {item}</li>
            ))}
          </ul>
        </div>
        <div className="bg-amber-50 rounded-xl p-2.5">
          <div className="font-semibold text-amber-700 mb-1">需留意</div>
          <ul className="space-y-1 text-amber-900">
            {(relationship.risks || profile.risks || []).slice(0, 3).map((item) => (
              <li key={item}>! {item}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Link
          to="/ai/personality"
          className="text-xs px-3 py-1.5 rounded-full border border-rose-200 text-rose-700"
        >
          完整 AI 人格
        </Link>
        <Link
          to="/discover"
          className="text-xs px-3 py-1.5 rounded-full border border-rose-200 text-rose-700"
        >
          去寻找对象
        </Link>
      </div>

      {valuesOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4"
          onClick={() => setValuesOpen(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-5 shadow-xl space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h4 className="font-bold text-gray-900">我的婚恋价值观</h4>
              <button type="button" className="text-sm text-gray-400" onClick={() => setValuesOpen(false)}>
                关闭
              </button>
            </div>
            <div className="text-sm text-gray-700 space-y-2 leading-relaxed">
              <p><span className="text-gray-400">家庭观：</span>{values.family_view || profile.family_view || "—"}</p>
              <p><span className="text-gray-400">婚姻期待：</span>{values.marriage_view || profile.marriage_view || "—"}</p>
              <p><span className="text-gray-400">金钱观：</span>{values.money_view || profile.values?.money_view || "—"}</p>
              <p><span className="text-gray-400">子女观：</span>{values.children_view || profile.values?.children_view || "—"}</p>
              <p><span className="text-gray-400">生活方式：</span>{values.lifestyle || profile.lifestyle || "—"}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AiConsultantPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const modeParam = (searchParams.get("mode") || "").toLowerCase();
  const presetQ = searchParams.get("q") || "";
  const userId = Number(localStorage.getItem("user_id"));
  const { isMarried } = useUserMaritalStatus();
  const [messages, setMessages] = useState([]);
  const [profile, setProfile] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [llmEnabled, setLlmEnabled] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [mode, setMode] = useState(
    modeParam === "marriage" || modeParam === "growth" ? modeParam : "dating"
  );
  const [agentInfo, setAgentInfo] = useState(null);
  const [surface, setSurface] = useState(
    ["chat", "companion"].includes(
      (searchParams.get("tab") || "").toLowerCase()
    )
      ? "companion"
      : "consult"
  ); // consult | companion
  const [consent, setConsent] = useState(null);
  const [showConsentCard, setShowConsentCard] = useState(false);
  const [suggestConsult, setSuggestConsult] = useState(false);
  const [showCompanionNudge, setShowCompanionNudge] = useState(false);
  const [insightToast, setInsightToast] = useState("");
  const [voiceSpeak, setVoiceSpeak] = useState(() => {
    try {
      return localStorage.getItem("companion_tts") !== "0";
    } catch {
      return true;
    }
  });
  const [voiceAutoSend, setVoiceAutoSend] = useState(() => {
    try {
      return localStorage.getItem("companion_voice_autosend") === "1";
    } catch {
      return false;
    }
  });
  const companionNudgeDismissed = useRef(
    typeof window !== "undefined" &&
      sessionStorage.getItem("companion_nudge_dismissed") === "1"
  );
  const bottomRef = useRef(null);
  const presetSentRef = useRef(false);
  const marriageMode = mode === "marriage" || isMarried;
  const growthMode = mode === "growth";
  const agentMode =
    growthMode ? "growth" : marriageMode ? "marriage" : modeParam || "dating";
  const isCompanion = surface === "companion";

  useSeo({
    title: isCompanion
      ? "和我聊聊 | AI婚恋智能体"
      : growthMode
      ? "AI成长教练 | AI婚恋智能体"
      : marriageMode
        ? "AI婚姻顾问 | AI婚恋智能体"
        : "我的AI咨询师 | AI婚恋智能体",
    metaDescription: isCompanion
      ? "想说什么都可以，我在听。温暖陪伴，不一定要有问题。"
      : growthMode
      ? "AI成长教练：沟通、情绪与关系经营能力练习。"
      : marriageMode
        ? "AI婚姻顾问：夫妻沟通、家庭关系、冲突修复与结婚证常识咨询。"
        : "与你的专属 AI 婚恋咨询师对话，生成个人婚恋画像报告。",
    path: "/ai/consultant",
  });

  useEffect(() => {
    return () => stopSpeaking();
  }, []);

  useEffect(() => {
    if (!isCompanion) stopSpeaking();
  }, [isCompanion]);

  useEffect(() => {
    if (!userId) {
      navigate(`/login?redirect=${encodeURIComponent("/ai/consultant")}`, {
        replace: true,
      });
    }
  }, [userId, navigate]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        if (surface === "companion") {
          const { res, data } = await apiJson(
            `/api/ai/companion/session?user_id=${userId}`
          );
          if (!res.ok) throw new Error(data?.detail || "加载失败");
          if (cancelled) return;
          setMessages(data.messages || []);
          setConsent(data.consent || null);
          setShowConsentCard(!!data.show_consent_card);
          setSuggestConsult(false);
        } else {
          const qs = new URLSearchParams({ user_id: String(userId) });
          if (modeParam === "marriage" || modeParam === "growth") {
            qs.set("agent_mode", modeParam);
          }
          const { res, data } = await apiJson(
            `/api/ai/consultant/session?${qs.toString()}`
          );
          if (!res.ok) throw new Error(data?.detail || "加载失败");
          if (cancelled) return;
          setMessages(data.messages || []);
          setProfile(data.profile || null);
          setLlmEnabled(!!data.llm_enabled);
          setMode(data.mode || modeParam || "dating");
          setAgentInfo(data.agent || null);
        }
      } catch (err) {
        console.error(err);
        alert(err.message || "加载失败");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userId, modeParam, surface]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const setBehaviorConsent = async (allowed) => {
    try {
      const { res, data } = await apiJson("/api/ai/companion/consent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, allowed }),
      });
      if (!res.ok) throw new Error(data?.detail || "保存失败");
      setConsent(data);
      setShowConsentCard(false);
    } catch (err) {
      alert(err.message || "保存失败");
    }
  };

  const sendMessage = async (e, presetText) => {
    e?.preventDefault();
    const text = String(presetText ?? input).trim();
    if (!text || sending) return;

    setInput("");
    setSending(true);
    setMessages((prev) => [
      ...prev,
      { id: `local-${Date.now()}`, role: "user", content: text },
    ]);

    try {
      if (isCompanion) {
        const { res, data } = await apiJson("/api/ai/companion/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId, message: text }),
        });
        if (!res.ok) throw new Error(data?.detail || "发送失败");
        setMessages(data.messages || []);
        setSuggestConsult(!!data.suggest_consult);
        if (data.consent) setConsent(data.consent);
        if (voiceSpeak && data.reply) {
          speakText(data.reply);
        }
        if (data.feature_updated && data.insight_preview) {
          setInsightToast(
            data.map_synced
              ? `已更新关系观察，并同步到关系地图：${data.insight_preview}`
              : `已更新关系观察：${data.insight_preview}`
          );
          setTimeout(() => setInsightToast(""), 5000);
        } else if (data.feature_updated) {
          setInsightToast("已更新你的关系行为观察（可在画像/关系地图中查看与修正）");
          setTimeout(() => setInsightToast(""), 4000);
        }
      } else {
        const { res, data } = await apiJson("/api/ai/consultant/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: userId,
            message: text,
            agent_mode: agentMode,
          }),
        });
        if (!res.ok) throw new Error(data?.detail || "发送失败");
        setMessages((prev) => {
          const withoutLocal = prev.filter((m) => !String(m.id).startsWith("local-"));
          const assistantMsg = {
            ...(data.message || {}),
            id: data.message?.id || `a-${Date.now()}`,
            role: "assistant",
            content: data.reply || data.message?.content || "",
            match_cards: data.match_cards || data.layer?.match_cards || [],
          };
          return [
            ...withoutLocal,
            { id: `u-${Date.now()}`, role: "user", content: text },
            assistantMsg,
          ];
        });
        if (data.profile) setProfile(data.profile);
        if (data.mode) setMode(data.mode);
        const userTurns =
          (data.messages || []).filter((m) => m.role === "user").length ||
          messages.filter((m) => m.role === "user").length + 1;
        if (
          !companionNudgeDismissed.current &&
          (data.suggest_companion ||
            data.has_profile ||
            marriageMode ||
            growthMode ||
            userTurns >= 3 ||
            !!data.profile)
        ) {
          setShowCompanionNudge(true);
        }
      }
    } catch (err) {
      alert(err.message || "发送失败");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (isCompanion) return;
    if (loading || sending || !presetQ || presetSentRef.current) return;
    presetSentRef.current = true;
    sendMessage(null, presetQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, presetQ, isCompanion]);

  const resetChat = async () => {
    if (isCompanion) {
      if (!window.confirm("确定重新开始陪聊？当前陪聊记录将归档。")) return;
      setSending(true);
      try {
        const { res, data } = await apiJson("/api/ai/companion/reset", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        });
        if (!res.ok) throw new Error(data?.detail || "重置失败");
        setMessages(data.messages || []);
        setConsent(data.consent || null);
        setShowConsentCard(!!data.show_consent_card);
      } catch (err) {
        alert(err.message || "重置失败");
      } finally {
        setSending(false);
      }
      return;
    }
    if (!window.confirm("确定重新开始？对话记录将被清空。")) return;
    setSending(true);
    try {
      const { res, data } = await apiJson("/api/ai/consultant/reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, agent_mode: agentMode }),
      });
      if (!res.ok) throw new Error(data?.detail || "重置失败");
      setMessages(data.messages || []);
      setProfile(data.profile || null);
      if (data.mode) setMode(data.mode);
      if (data.agent) setAgentInfo(data.agent);
    } catch (err) {
      alert(err.message || "重置失败");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 flex flex-col">
      <Header />

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-8 space-y-4">
        <AiModuleTabs groupKey="advisor" />
        <div className="grid lg:grid-cols-12 gap-6">
        <section className="lg:col-span-9 bg-white rounded-2xl shadow-sm border border-rose-100 flex flex-col min-h-[70vh] order-1">
          <div className="px-5 py-4 border-b space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {isCompanion
                    ? "☕ 和我聊聊"
                    : growthMode
                      ? "我的 AI 成长教练"
                      : marriageMode
                        ? "我的 AI 婚姻顾问"
                        : "我的 AI 咨询师"}
                </h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  {isCompanion
                    ? "想说什么都可以，我在听。"
                    : growthMode
                      ? "能力练习 · 成长陪伴 · 课程建议"
                      : marriageMode
                        ? "沟通修复 · 家庭关系 · 结婚证常识"
                        : "了解你 · 分析你 · 帮助你找到适合的人"}
                  {!isCompanion && agentInfo?.name ? ` · ${agentInfo.name}` : ""}
                  {!isCompanion && (llmEnabled ? " · 大模型已启用" : " · 规则引擎模式")}
                </p>
              </div>
              <button
                type="button"
                onClick={resetChat}
                className="text-xs text-gray-500 hover:text-rose-600"
              >
                {isCompanion ? "重新聊聊" : "重新访谈"}
              </button>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSurface("consult")}
                className={`px-4 py-1.5 rounded-full text-sm border ${
                  !isCompanion
                    ? "bg-rose-500 text-white border-rose-500"
                    : "bg-white text-gray-600 border-gray-200"
                }`}
              >
                AI咨询
              </button>
              <button
                type="button"
                onClick={() => setSurface("companion")}
                className={`px-4 py-1.5 rounded-full text-sm border ${
                  isCompanion
                    ? "bg-amber-600 text-white border-amber-600"
                    : "bg-white text-gray-600 border-gray-200"
                }`}
              >
                和我聊聊
              </button>
            </div>
            {isCompanion && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {ttsSupported() && (
                  <button
                    type="button"
                    onClick={() => {
                      setVoiceSpeak((v) => {
                        const next = !v;
                        try {
                          localStorage.setItem("companion_tts", next ? "1" : "0");
                        } catch {
                          /* ignore */
                        }
                        if (!next) stopSpeaking();
                        return next;
                      });
                    }}
                    className={`px-3 py-1 rounded-full text-[11px] border ${
                      voiceSpeak
                        ? "bg-amber-50 border-amber-300 text-amber-900"
                        : "bg-white border-gray-200 text-gray-500"
                    }`}
                  >
                    {voiceSpeak ? "🔊 语音播报开" : "🔇 语音播报关"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setVoiceAutoSend((v) => {
                      const next = !v;
                      try {
                        localStorage.setItem(
                          "companion_voice_autosend",
                          next ? "1" : "0"
                        );
                      } catch {
                        /* ignore */
                      }
                      return next;
                    });
                  }}
                  className={`px-3 py-1 rounded-full text-[11px] border ${
                    voiceAutoSend
                      ? "bg-violet-50 border-violet-300 text-violet-900"
                      : "bg-white border-gray-200 text-gray-500"
                  }`}
                >
                  {voiceAutoSend ? "🎤 说完自动发送" : "🎤 说完仅填入"}
                </button>
                <Link
                  to="/ai/relationship-memory"
                  className="px-3 py-1 rounded-full text-[11px] border bg-white text-gray-600"
                >
                  📔 关系记忆
                </Link>
                <Link
                  to="/ai/relationship-map"
                  className="px-3 py-1 rounded-full text-[11px] border bg-white text-violet-700"
                >
                  🗺️ 关系地图
                </Link>
              </div>
            )}
          </div>

          {isCompanion && showConsentCard && (
            <div className="mx-4 mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 space-y-2">
              <p className="text-xs text-amber-900 leading-relaxed">
                {consent?.copy ||
                  "授权后只会提取结构化关系行为观察，用于你自己的关系画像与关系地图，不会把聊天原文交给模拟或他人。"}
              </p>
              {(consent?.details || []).length > 0 && (
                <ul className="text-[11px] text-amber-900/80 space-y-0.5 list-disc pl-4">
                  {consent.details.slice(0, 4).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setBehaviorConsent(true)}
                  className="px-3 py-1.5 rounded-full bg-violet-600 text-white text-xs"
                >
                  允许用于我的关系分析
                </button>
                <button
                  type="button"
                  onClick={() => setBehaviorConsent(false)}
                  className="px-3 py-1.5 rounded-full border text-xs"
                >
                  暂时不使用
                </button>
                <Link
                  to="/ai/behavior-profile"
                  className="px-3 py-1.5 rounded-full border text-xs text-violet-700"
                >
                  先看看会存什么
                </Link>
              </div>
            </div>
          )}

          {isCompanion && insightToast && (
            <div className="mx-4 mt-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 flex flex-wrap items-center justify-between gap-2">
              <span>{insightToast}</span>
              <span className="flex gap-2 shrink-0">
                <Link to="/ai/behavior-profile" className="underline">
                  查看画像
                </Link>
                <Link to="/ai/relationship-map" className="underline">
                  关系地图
                </Link>
              </span>
            </div>
          )}

          {isCompanion && consent?.allowed && !showConsentCard && (
            <div className="mx-4 mt-2 flex flex-wrap gap-2 text-[11px] text-gray-500">
              <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100">
                已授权行为观察
              </span>
              <Link to="/ai/behavior-profile" className="px-2 py-1 rounded-full border hover:border-violet-300">
                查看 / 关闭授权
              </Link>
              <Link to="/ai/relationship-map" className="px-2 py-1 rounded-full border hover:border-violet-300">
                修正关系地图
              </Link>
            </div>
          )}

          {isCompanion && suggestConsult && (
            <div className="mx-4 mt-2 rounded-xl border border-rose-200 bg-rose-50/80 px-3 py-2.5 space-y-2">
              <p className="text-xs text-rose-900">
                听起来你好像在找办法。你现在更想要哪一种？
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSuggestConsult(false)}
                  className="px-3 py-1.5 rounded-full border text-xs bg-white"
                >
                  💬 陪我聊聊
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSurface("consult");
                    setSuggestConsult(false);
                    setShowCompanionNudge(false);
                  }}
                  className="px-3 py-1.5 rounded-full bg-rose-500 text-white text-xs"
                >
                  🔍 帮我分析
                </button>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {loading ? (
              <p className="text-sm text-gray-400 text-center py-10">
                {isCompanion ? "正在准备陪伴空间…" : "正在连接 AI 咨询师…"}
              </p>
            ) : (
              messages.map((m) => (
                <div
                  key={m.id}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[90%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-rose-500 text-white rounded-br-md whitespace-pre-wrap"
                        : "bg-gray-50 text-gray-800 border border-gray-100 rounded-bl-md"
                    }`}
                  >
                    <div className="whitespace-pre-wrap">{m.content}</div>
                    {m.role === "assistant" && (
                      <MatchRecommendCards cards={m.match_cards} />
                    )}
                  </div>
                </div>
              ))
            )}
            {sending && (
              <div className="text-xs text-gray-400 pl-2">
                {isCompanion ? "我在听…" : "AI 咨询师正在思考…"}
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {!isCompanion && showCompanionNudge && (
            <div className="mx-4 mb-2 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-rose-50 px-4 py-3 space-y-2">
              <p className="text-sm text-amber-950 font-medium">
                如果你暂时不想继续分析，我们也可以随便聊聊。
              </p>
              <p className="text-xs text-amber-800/80">
                不一定要有问题。想说话的时候，就来聊聊。
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCompanionNudge(false);
                    setSurface("companion");
                  }}
                  className="px-4 py-1.5 rounded-full bg-amber-600 text-white text-xs font-semibold"
                >
                  和我聊聊
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCompanionNudge(false);
                    companionNudgeDismissed.current = true;
                    sessionStorage.setItem("companion_nudge_dismissed", "1");
                  }}
                  className="px-3 py-1.5 rounded-full border text-xs text-gray-500"
                >
                  稍后再说
                </button>
              </div>
            </div>
          )}

          {!isCompanion && (
          <div className="px-4 pb-2 flex flex-wrap gap-2">
            {(marriageMode
              ? [
                  "夫妻总是吵架怎么办",
                  "结婚证/结婚登记要注意什么",
                  "和双方父母相处有矛盾",
                ]
              : [
                  "帮我找适合的对象",
                  "重新分析我的画像",
                  "沟通矛盾怎么办",
                ]
            ).map((chip) => (
              <button
                key={chip}
                type="button"
                disabled={sending || loading}
                onClick={() => sendMessage(null, chip)}
                className="px-3 py-1.5 rounded-full border border-rose-200 text-rose-600 text-xs hover:bg-rose-50 disabled:opacity-50"
              >
                {chip}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setSurface("companion")}
              className="px-3 py-1.5 rounded-full border border-amber-200 text-amber-800 text-xs hover:bg-amber-50"
            >
              不想分析了，和我聊聊
            </button>
          </div>
          )}

          {isCompanion && (
            <div className="px-4 pb-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
              {consent?.allowed
                ? "已授权：可提取关系行为特征用于你自己的关系模拟"
                : "未授权行为分析：陪聊仍可正常使用"}
              <Link
                to="/ai/behavior-profile"
                className="text-violet-600 underline ml-1"
              >
                我的关系行为画像
              </Link>
              <Link to="/ai/simulation" className="text-violet-600 underline ml-1">
                去关系模拟
              </Link>
            </div>
          )}

          <form
            id="ai-consultant-form"
            onSubmit={sendMessage}
            className="p-4 border-t flex gap-2 items-center"
          >
            <SpeechInputButton
              disabled={sending || loading}
              maxLength={2000}
              getPrefix={() => input}
              onResult={(text, meta) => {
                if (!text) return;
                setInput(text);
                if (isCompanion && voiceAutoSend && meta?.final) {
                  sendMessage(null, text);
                }
              }}
            />
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value.slice(0, 2000))}
              rows={1}
              maxLength={2000}
              placeholder={
                isCompanion
                  ? voiceAutoSend
                    ? "点麦克风说话，说完再点一次即可发送…"
                    : "输入或点麦克风说话；可开启「说完自动发送」"
                  : marriageMode
                    ? "说说你的婚姻困扰，或点麦克风说话…"
                    : "用自己的话回答，或点麦克风语音输入…"
              }
              className="flex-1 border border-gray-200 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 resize-y min-h-[48px] max-h-40"
              disabled={sending || loading}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(e);
                }
              }}
            />
            <button
              type="submit"
              disabled={sending || loading || !input.trim()}
              className="px-5 py-3 rounded-full bg-rose-500 text-white text-sm font-medium disabled:opacity-50 hover:bg-rose-600"
            >
              发送
            </button>
          </form>
        </section>

        <aside className="lg:col-span-3 space-y-4 order-2">
          {marriageMode ? (
            <div className="rounded-2xl bg-white border border-rose-100 p-5 shadow-sm space-y-3">
              <h3 className="font-bold text-gray-900">AI 婚姻顾问能帮你</h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>· 夫妻沟通与冲突处理</li>
                <li>· 原生家庭边界与相处</li>
                <li>· 金钱、育儿与责任分工</li>
                <li>· 结婚登记 / 结婚证常识参考</li>
              </ul>
              <p className="text-xs text-gray-400">
                涉及法律效力请以当地民政局或律师意见为准。也可预约「真人咨询」。
              </p>
              <div className="flex flex-wrap gap-3 pt-1">
                <Link
                  to="/consultants"
                  className="inline-block px-4 py-2 rounded-full border border-rose-300 text-rose-600 text-sm"
                >
                  预约真人咨询师
                </Link>
                <Link
                  to="/center/ai-memory"
                  className="inline-block px-4 py-2 rounded-full border border-gray-200 text-gray-600 text-sm hover:border-rose-300"
                >
                  管理 AI 记忆
                </Link>
              </div>
            </div>
          ) : profile ? (
            <>
              <ProfileReportCard profile={profile} />
              <div className="flex flex-wrap justify-center gap-3 text-sm">
                <Link to="/ai/personality" className="text-rose-600 underline">
                  AI 人格档案
                </Link>
                <Link to="/ai/twin" className="text-rose-600 underline">
                  数字分身
                </Link>
                <Link to="/ai/relationship-map" className="text-violet-600 underline">
                  AI 关系地图
                </Link>
                <Link to="/center/ai-memory" className="text-rose-600 underline">
                  AI 记忆
                </Link>
              </div>
            </>
          ) : (
            <div className="rounded-2xl bg-white border border-dashed border-rose-200 p-6 text-center">
              <h3 className="font-bold text-gray-800 mb-2">画像报告待生成</h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                完成深度访谈后，将自动生成《个人婚恋画像报告》与「AI 人格档案」，并可用于数字分身对话。
              </p>
              <button
                type="button"
                className="mt-4 text-sm text-rose-600 underline disabled:opacity-50"
                disabled={generating}
                onClick={async () => {
                  setGenerating(true);
                  try {
                    const { res, data } = await apiJson("/api/ai/profile/generate", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ user_id: userId, force: true }),
                    });
                    if (!res.ok) throw new Error(data?.detail || "生成失败");
                    setProfile(data.profile);
                    if (!companionNudgeDismissed.current) {
                      setShowCompanionNudge(true);
                    }
                  } catch (err) {
                    alert(err.message || "生成失败");
                  } finally {
                    setGenerating(false);
                  }
                }}
              >
                {generating ? "正在生成画像报告…" : "基于现有资料快速生成"}
              </button>
              <Link
                to="/center/ai-memory"
                className="block mt-3 text-sm text-gray-500 underline"
              >
                管理我的 AI 记忆
              </Link>
            </div>
          )}

          <div className="rounded-2xl bg-white p-5 border border-amber-100 text-sm text-gray-600 space-y-3">
            <h4 className="font-semibold text-gray-800">想轻松一点？</h4>
            <p className="text-xs text-gray-500 leading-relaxed">
              不一定要有问题。想说话的时候，可以切换到「和我聊聊」。
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setSurface("companion")}
                className="px-4 py-2 rounded-full bg-amber-600 text-white text-xs font-semibold"
              >
                和我聊聊
              </button>
              <Link
                to="/ai/behavior-profile"
                className="px-4 py-2 rounded-full border border-violet-200 text-violet-700 text-xs"
              >
                关系行为画像
              </Link>
            </div>
          </div>

          <div className="rounded-2xl bg-white p-5 border border-gray-100 text-sm text-gray-600 space-y-2">
            <h4 className="font-semibold text-gray-800">平台理念</h4>
            <p>
              {marriageMode
                ? "婚姻同样需要学习与陪伴。AI 帮助你更好地经营关系，成为更好的伴侣。"
                : "从「找对象」升级为：AI 帮助你认识自己、找到适合的人，并成长为更适合进入婚姻的人。"}
            </p>
            <p className="text-xs text-gray-400">
              真人咨询师服务仍可在「婚姻咨询」入口预约，与 AI 互补。
            </p>
          </div>
        </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
